import { AlertCircle, ChevronLeft, X } from "lucide-react";
import type { ReactNode } from "react";

import { EmailLoginForm } from "@/components/auth/EmailLoginForm";
import { AuthDivider, OAuthButtons } from "@/components/auth/OAuthButtons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GestureBottomSheetPresence } from "@/components/ui/GestureBottomSheet";
import { useAuthStore } from "@/store/authStore";

/** Dims and blurs the screen behind any auth sheet. */
export const AUTH_SHEET_OVERLAY_CLASS =
  "bg-[#0b1020]/45 backdrop-blur-[10px] backdrop-saturate-150";

/** Rounded, light panel with room for the home indicator. */
export const AUTH_SHEET_PANEL_CLASS =
  "rounded-t-[28px] border-transparent bg-[#f6f6f9] text-[#212330] shadow-[0_-24px_60px_rgba(11,16,32,0.28)]";

/**
 * Shared chrome for the auth sheets: a grabber, an optional back arrow, a
 * centred title and a close button. `onBack` is omitted on a flow's first step,
 * where closing is the only way out.
 */
export function AuthSheetShell({
  title,
  subtitle,
  onBack,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col px-7 pb-[max(2.25rem,env(safe-area-inset-bottom))]">
      <div
        aria-hidden="true"
        className="mx-auto mt-3 h-1.5 w-11 shrink-0 rounded-full bg-[#d4d7e3]"
      />

      <div className="relative flex min-h-9 items-center justify-center pt-5">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="absolute left-0 inline-flex size-9 items-center justify-center rounded-full text-[#626f95] transition hover:bg-black/5 hover:text-[#212330]"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        ) : null}
        <h2 className="text-[17px] font-semibold tracking-[-0.01em]">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-0 inline-flex size-9 items-center justify-center rounded-full bg-black/5 text-[#626f95] transition hover:bg-black/10 hover:text-[#212330]"
        >
          <X className="h-4.5 w-4.5" />
        </button>
      </div>

      {subtitle ? (
        <p className="mt-2 text-center text-sm leading-6 text-[#626f95]">
          {subtitle}
        </p>
      ) : null}

      <div className="flex flex-col pt-7">{children}</div>
    </div>
  );
}

/**
 * Sign-in bottom sheet opened from `/login`. Renders the same `EmailLoginForm`
 * as the `/login/email` screen, which still exists for deep links and the
 * password-reset return trip.
 */
export function SignInSheet({
  isOpen,
  onOpenChange,
  onSwitchToSignup,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSwitchToSignup: () => void;
}) {
  const error = useAuthStore(s => s.error);

  return (
    <GestureBottomSheetPresence
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      presenceKey="sign-in-sheet"
      aria-label="Log in with email"
      panelClassName={AUTH_SHEET_PANEL_CLASS}
      overlayClassName={AUTH_SHEET_OVERLAY_CLASS}
    >
      <AuthSheetShell
        title="Welcome back"
        subtitle="Choose how you'd like to log in."
        onClose={() => onOpenChange(false)}
      >
        {error ? (
          <Alert className="mb-6 border-[#fecaca] bg-[#fef2f2] py-3 text-[#ef4444]">
            <AlertCircle className="h-4 w-4 translate-y-0 text-[#ef4444]" />
            <AlertDescription className="text-sm leading-5 font-medium text-[#ef4444]">
              {error}
            </AlertDescription>
          </Alert>
        ) : null}
        <OAuthButtons />
        <AuthDivider />
        <EmailLoginForm onSignupClick={onSwitchToSignup} />
      </AuthSheetShell>
    </GestureBottomSheetPresence>
  );
}
